import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const safeNum = (v) => {
  const str = String(v).replace(/−/g, '-').replace(/–/g, '-');
  const n = Number(str.replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const StockAdjustModal = ({
  itemId,
  itemLabel,
  initialStock,
  onDone,
  onClose,
}) => {
  const { t } = useLanguage();
  const [change, setChange] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [currentStock, setCurrentStock] = useState(safeNum(initialStock));
  const modalRef = useRef(null);
  const changeInputRef = useRef(null);

  const adjusted = Math.max(0, currentStock + safeNum(change));

  useEffect(() => {
    // Focus trap and initial focus
    if (changeInputRef.current) {
      changeInputRef.current.focus();
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && !isSaving && adjusted >= 0) {
        handleSave();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isSaving, adjusted]);

  const handleSave = async () => {
    if (adjusted < 0) return;
    setIsSaving(true);
    setError('');
    try {
      // Call onDone with the new stock value
      // The parent component (App.js) will handle updating state and syncing to Supabase
      // IMPORTANT: Wait for onDone to complete (it's async and syncs to Supabase)
      await onDone(adjusted);
      onClose();
    } catch (err) {
      setError(t('failedToUpdateStock'));
      console.error('Stock update error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const quickAdjust = (delta) => {
    setChange((prev) => Math.max(-1000000, Math.min(1000000, safeNum(prev) + delta)));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="flex justify-between items-center p-6 border-b">
          <h2 id="modal-title" className="text-xl font-bold text-gray-800">
            {itemLabel}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label={t('close')}
          >
            ✕
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('currentStock')}
            </label>
            <p className="text-lg font-semibold">{currentStock}</p>
          </div>
          <div>
            <label
              htmlFor="change-input"
              className="block text-sm font-medium text-gray-700"
            >
              {t('stockChange')}
            </label>
            <input
              id="change-input"
              ref={changeInputRef}
              type="number"
              value={change}
              onChange={(e) => {
                const val = safeNum(e.target.value);
                setChange(Math.max(-1000000, Math.min(1000000, val)));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isSaving && adjusted >= 0) {
                  handleSave();
                }
              }}
              min="-1000000"
              max="1000000"
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500"
              aria-describedby="adjusted-stock"
            />
            <div className="flex gap-2 mt-3 flex-wrap">
              {[-50, -10, -5, -1, 1, 5, 10, 50].map((delta) => (
                <button
                  key={delta}
                  onClick={() => quickAdjust(delta)}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  disabled={isSaving}
                >
                  {delta > 0 ? '+' : ''}{delta}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('adjustedStock')}
            </label>
            <p
              id="adjusted-stock"
              className={`text-lg font-semibold ${
                adjusted < 0 ? 'text-red-600' : 'text-green-600'
              }`}
            >
              {adjusted}
            </p>
            {adjusted < 0 && (
              <p className="text-sm text-red-600 mt-1">
                {t('cannotSetStockBelowZero')}
              </p>
            )}
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="p-6 bg-gray-50 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg"
            disabled={isSaving}
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || adjusted < 0}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
          >
            {isSaving ? t('saving') : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockAdjustModal;
